import { IkeFieldType, type SubFormSpanTypeState, SubFormType } from '@ikegps/core-api';
import { IkeId } from '../../../../id';
import { FormModelFieldSelectList } from '../../form.select.list';
import { isValidMidSpanIkePhotoField, isValidSpanTypeField, isValidSpanVectorField } from './form.subform.span';
import { SpanType } from '../../../pole/integrations';
import { FormModelState } from '../../form.state';
import { type FormFieldSubFormState, FormModelFieldSubForm } from '../form.subform';
import { BaseForm, PLAForm } from '../../../pole';
import { LOCKED_MODEL_POLE_FIELDS } from '../../form.field.lock';

export class SpanSubFormMutator {
	private field: FormModelFieldSubForm;

	constructor(field: FormModelFieldSubForm) {
		this.field = field;
	}

	private get state(): FormModelState {
		return this.field.getForm().state;
	}

	/**
	 * Set the SubForm Type of this field to SPAN while linking the existing vector, span type,
	 * and mid span photo fields.
	 * Creates vector, type, and mid span photo fields if they don't exist.
	 */
	public enable() {
		let vectorField = this.field.fields.find(isValidSpanVectorField);
		let typeField = this.field.fields.find(isValidSpanTypeField);
		let midSpanPhotoField = this.field.fields.find(isValidMidSpanIkePhotoField);

		if (!vectorField) {
			vectorField = this.addVectorField();
		}
		if (!typeField) {
			typeField = this.addSpanTypeField();
		}
		if (!midSpanPhotoField) {
			midSpanPhotoField = this.addMidSpanPhotoField();
		}

		this.setFieldReferences(vectorField.id, typeField.id, midSpanPhotoField.id);
		this.updateLockState(true);
	}

	/**
	 * Disable SPAN subform type, unlocking all span fields and clearing state.
	 */
	public disable() {
		const form = this.field.getForm();
		const isCustomForm = !BaseForm.isBaseForm(form) && !PLAForm.isPLAForm(form);
		this.updateLockState(false);

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.NONE,
			spanTypeState: undefined, // Clear to avoid stale field references
			...(isCustomForm && { locked: false }),
		} satisfies FormFieldSubFormState);
	}

	/**
	 * Set the SubForm Type of this field to SPAN.
	 *
	 * @param vectorFieldId The Span Vector field ID to use.
	 * @param typeFieldId The Span Type field ID to use.
	 * @param midSpanIkePhotoFieldId Optional Mid Span IKE Photo field ID for two-way linking.
	 * @throws If any of the given field IDs do not exist in this subform.
	 */
	public setFieldReferences(vectorFieldId: string, typeFieldId: string, midSpanIkePhotoFieldId?: string) {
		if (!this.field.hasField(vectorFieldId)) {
			throw new Error(`Vector field '${vectorFieldId}' not found`);
		}
		if (!this.field.hasField(typeFieldId)) {
			throw new Error(`Span Type field '${typeFieldId}' not found`);
		}
		if (midSpanIkePhotoFieldId && !this.field.hasField(midSpanIkePhotoFieldId)) {
			throw new Error(`Mid span photo field '${midSpanIkePhotoFieldId}' not found`);
		}
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.SPAN,
			spanTypeState: {
				vectorFieldId,
				typeFieldId,
				midSpanIkePhotoFieldId,
			},
			locked: true,
		} satisfies FormFieldSubFormState);

		this.updateLockState(true);
	}

	public selectVectorField(fieldId: string) {
		this.updateFieldReference('vectorFieldId', fieldId);
	}

	public selectSpanTypeField(fieldId: string) {
		this.updateFieldReference('typeFieldId', fieldId);
	}

	public selectMidSpanIkePhotoField(fieldId: string) {
		this.updateFieldReference('midSpanIkePhotoFieldId', fieldId);
	}

	private updateFieldReference(key: keyof SubFormSpanTypeState, newFieldId: string) {
		const currentState = this.field.stateValue.spanTypeState;

		if (!currentState) {
			return;
		}

		const oldFieldId = currentState?.[key];

		const newState: SubFormSpanTypeState = {
			...currentState,
			[key]: newFieldId,
		};

		if (newState.vectorFieldId && newState.typeFieldId && newFieldId) {
			this.state.setFieldValue(this.field.fieldId, {
				...this.field.stateValue,
				subFormType: SubFormType.SPAN,
				spanTypeState: newState,
				locked: true,
			} satisfies FormFieldSubFormState);
		}

		const oldField = oldFieldId ? this.field.fields.find((f) => f.id === oldFieldId) : undefined;
		const newField = this.field.fields.find((f) => f.id === newFieldId);

		if (oldField && !LOCKED_MODEL_POLE_FIELDS[oldField.id]) {
			oldField.Mutator.setLockState(false);
		}
		newField?.Mutator.setLockState(true);
	}

	public addMidSpanPhotoField() {
		return this.field.createField(IkeFieldType.TRUE_SIZE, {
			id: IkeId.generate(),
			displayName: 'Mid Span IKE Photo',
			locked: true,
			isGenerated: true,
		});
	}

	private addVectorField() {
		return this.field.createField(IkeFieldType.VECTOR, {
			id: IkeId.generate(),
			displayName: 'Span Length',
			locked: true,
			isGenerated: true,
		});
	}

	private addSpanTypeField() {
		const spanTypeField = this.field.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: IkeId.generate(),
			displayName: 'Type',
			title: true,
			locked: true,
			isGenerated: true,
		});

		spanTypeField.Mutator.option({
			text: 'Fore Span',
			value: SpanType.NextPole,
		});
		spanTypeField.Mutator.option({
			text: 'Back Span',
			value: SpanType.PreviousPole,
		});
		spanTypeField.Mutator.option({
			text: 'Other',
			value: SpanType.Other,
		});
		spanTypeField.Mutator.option({
			text: 'Building',
			value: SpanType.Building,
		});

		return spanTypeField;
	}

	private updateLockState(isLocked: boolean) {
		const vectorField = this.field.fields.find((field) => field.id === this.field.subFormSpanTypeState?.vectorFieldId);
		const typeField = this.field.fields.find((field) => field.id === this.field.subFormSpanTypeState?.typeFieldId);
		const midSpanIkePhotoField = this.field.fields.find(
			(field) => field.id === this.field.subFormSpanTypeState?.midSpanIkePhotoFieldId
		);

		if (isLocked) {
			vectorField?.Mutator.setLockState(isLocked);
			typeField?.Mutator.setLockState(isLocked);
			midSpanIkePhotoField?.Mutator.setLockState(isLocked);
			return;
		}

		if (vectorField && (!LOCKED_MODEL_POLE_FIELDS[vectorField.id] || vectorField.isGenerated)) {
			vectorField.Mutator.setLockState(isLocked);
		}
		if (typeField && (!LOCKED_MODEL_POLE_FIELDS[typeField.id] || typeField?.isGenerated)) {
			typeField.Mutator.setLockState(isLocked);
		}
		if (
			midSpanIkePhotoField &&
			(!LOCKED_MODEL_POLE_FIELDS[midSpanIkePhotoField.id] || midSpanIkePhotoField?.isGenerated)
		) {
			midSpanIkePhotoField.Mutator.setLockState(isLocked);
		}
	}
}
